import { RootState } from '@next/core-lib/app';
import { DataSeries } from '@next/core-lib/models';
import { Point } from 'highcharts';

export interface WithinCyclePerformanceState extends RootState {
  selectedCycleId?: number;
  selectedbarnId?: number;
  refValueSheetDataSeries?: DataSeries<number, number>;
  wdoDataSeries?: DataSeries<number, number>[];
  growthPredictionDataSeries?: DataSeries<number, number>[];
  observations: ObservationsRegistration[];
}

export enum WithinCyclePerformanceKpisEnum {
  INDOOR_TEMPERATURE = 'IndoorTemperature',
  INDOOR_HUMIDITY = 'IndoorHumidity',
  OUTDOOR_TEMPERATURE = 'OutdoorTemperature',
  OUTDOOR_HUMIDITY = 'OutdoorHumidity',
  MORTALITY_PERCENTAGE = 'MortalityPercentage',
  FEED_INTAKE_PER_CHICK = 'FeedIntakePerChick',
  WATER_INTAKE_PER_CHICK = 'WaterIntakePerChick',
  WEIGHT_GAIN_PER_CHICK = 'WeightGainPerChick',
  WATER_TO_FEED_RATIO = 'WaterToFeedRatio',
  FEED_CONVERSION_RATE = 'FeedConversionRate',
  LITTER_QUALITY = 'LitterQuality',
  LITTER_QUALITY_CAKED = 'LitterQualityCaked',
  LITTER_QUALITY_LOOSE = 'LitterQualityLoose',
  LITTER_QUALITY_WET = 'LitterQualityWet',
  BODY_WEIGHT = 'BodyWeight',
  INDOOR_OUTDOOR_TEMPERATURE = 'IndoorOutdoorTemperature',
  INDOOR_OUTDOOR_HUMIDITY = 'IndoorOutdoorHumidity',
}

export interface WithinCyclePerformanceKpi {
  kpi: WithinCyclePerformanceKpisEnum;
  uom: string;
  hasCumulativeOption: boolean;
  numberOfDecimals: number;
  showInKpiSelector: boolean;
}

export interface ObservationsRegistration {
  cycleId: number;
  barnId: number;
  intervalType: IntervalType;
  intervalValue: number;
  mortality?: number;
  selection?: number;
  selectionSmall?: number;
  selectionLegProblems?: number;
  feedIntakeInKg?: number;
  waterIntakeInL?: number;
  indoorTemperatureInCelsius?: number;
  outdoorTemperatureInCelsius?: number;
  indoorHumidityInPercentages?: number;
  outdoorHumidityInPercentages?: number;
  chickWeightInGrams?: number;
  litterQualityLoosePercentage?: number;
  litterQualityCakedPercentage?: number;
  litterQualityWetPercentage?: number;
}

export enum IntervalType {
  Day = 'Day',
  Week = 'Week',
}

export interface DayPoint extends Point {
  day: number;
}

export interface GetObservationsResponse {
  observations: ObservationsRegistration[];
}

export interface UpsertObservationResponse {
  id: number;
  isOffline?: boolean;
}

export interface WeeklyReportValueObject {
  actualValue: string;
  referenceValueSheetValue: string;
}

export const WithinCyclePerformanceKpis: WithinCyclePerformanceKpi[] = [
  {
    kpi: WithinCyclePerformanceKpisEnum.INDOOR_TEMPERATURE,
    hasCumulativeOption: false,
    uom: '°C',
    numberOfDecimals: 1,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.OUTDOOR_TEMPERATURE,
    hasCumulativeOption: false,
    uom: '°C',
    numberOfDecimals: 1,
    showInKpiSelector: false,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.INDOOR_OUTDOOR_TEMPERATURE,
    hasCumulativeOption: false,
    uom: '°C',
    numberOfDecimals: 1,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.INDOOR_HUMIDITY,
    hasCumulativeOption: false,
    uom: '%',
    numberOfDecimals: 1,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.OUTDOOR_HUMIDITY,
    hasCumulativeOption: false,
    uom: '%',
    numberOfDecimals: 1,
    showInKpiSelector: false,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.INDOOR_OUTDOOR_HUMIDITY,
    hasCumulativeOption: false,
    uom: '%',
    numberOfDecimals: 1,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.MORTALITY_PERCENTAGE,
    hasCumulativeOption: true,
    uom: '%',
    numberOfDecimals: 2,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.FEED_INTAKE_PER_CHICK,
    hasCumulativeOption: true,
    uom: 'g',
    numberOfDecimals: 1,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.WATER_INTAKE_PER_CHICK,
    hasCumulativeOption: true,
    uom: 'ml',
    numberOfDecimals: 1,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.WEIGHT_GAIN_PER_CHICK,
    hasCumulativeOption: true,
    uom: 'g',
    numberOfDecimals: 1,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.WATER_TO_FEED_RATIO,
    hasCumulativeOption: true,
    uom: 'ml/g',
    numberOfDecimals: 2,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.FEED_CONVERSION_RATE,
    hasCumulativeOption: true,
    uom: '',
    numberOfDecimals: 3,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.LITTER_QUALITY,
    hasCumulativeOption: false,
    uom: '%',
    numberOfDecimals: 1,
    showInKpiSelector: true,
  },
  {
    kpi: WithinCyclePerformanceKpisEnum.BODY_WEIGHT,
    hasCumulativeOption: false,
    uom: 'g',
    numberOfDecimals: 1,
    showInKpiSelector: true,
  },
];
